import React, { useState } from "react"
import {
    Card,
    CardContent,
    CardMedia,
    ClickAwayListener,
    Grid,
    Grow,
    IconButton,
    MenuList,
    MenuItem,
    Paper,
    Popper,
    Skeleton,
    Typography,
} from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faImage } from "@fortawesome/pro-solid-svg-icons"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import { makeStyles } from "tss-react/mui"
import { enqueueSnackbar } from "notistack"

import { clearDelfiSigns, DigitalSignageResponse, fetchDigitalSignageList, MenuPlaningQuery } from "../menuPlanningApi"
import { DigitalSignage as TDigitalSignage } from "../menuPlanningTypes"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { tailwindPalette } from "../../../../theme/tailwindPalette"
import { SignsStatusChip } from "../components/digitalSignage/SignsStatusChip"
import { DigitalSignageDialog } from "../components/digitalSignage/DigitalSignageDetailsDialog"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { useHoverMenu } from "../../../../../shared/hooks/useHoverMenu"

const useStyles = makeStyles()(() => ({
    card: {
        width: "21rem",
        height: "fit-content",
        backgroundColor: "transparent",
        boxShadow: "none",
        position: "relative",
    },
    cardContent: {
        padding: "1rem 0",
    },
    emptyImage: {
        height: "13rem",
        background: tailwindPalette.gray[300],
        justifyContent: "center",
        alignItems: "center",
    },
    image: {
        minHeight: "13rem",
    },
    statusChip: {
        position: "absolute",
        top: "1rem",
        left: "1rem",
    },
    menuPaper: {
        minWidth: "10rem",
        boxShadow: "-1px 3px 10px 0 rgba(0, 0, 0, 0.14)",
    },
    popper: {
        zIndex: 1300,
    },
}))

const selectDigitalSignage = (data: DigitalSignageResponse) => data?.signs

export function DigitalSignage() {
    const { merchantId } = useMerchant()
    const { classes } = useStyles()

    const { data, isLoading } = useApiQuery(fetchDigitalSignageList, {
        queryName: MenuPlaningQuery.DIGITAL_SIGNAGE,
        dependencies: {
            merchantId,
        },
        select: selectDigitalSignage,
    })

    const { mutateAsync } = useApiMutation(clearDelfiSigns, {
        async onSuccess(_, data) {
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    const hoverMenu = useHoverMenu()
    const [selectedItem, setSelectedItem] = useState<TDigitalSignage | null>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: TDigitalSignage) => {
        setSelectedItem(item)
        hoverMenu.handleOpen(event)
    }

    const handleViewDetails = () => {
        setIsModalOpen(true)
        hoverMenu.handleClose()
    }

    const handleClearSign = async (id: number) => {
        hoverMenu.handleClose()
        await mutateAsync({ id })
    }

    const handleModalClose = () => {
        setIsModalOpen(false)
    }

    if (isLoading) {
        return (
            <Grid container gap="2rem">
                {Array.from({ length: 3 }).map((_, index) => (
                    <Card key={index} className={classes.card}>
                        <Skeleton variant="rectangular" className={classes.image} />
                        <CardContent>
                            <Grid container spacing={1}>
                                <Grid item xs={6}>
                                    <Skeleton variant="text" />
                                </Grid>
                                <Grid item xs={4} display="flex" justifyContent="flex-end" alignItems="center">
                                    <Skeleton variant="text" width="50%" />
                                </Grid>
                                <Grid item xs={2} display="flex" justifyContent="flex-end" alignItems="center">
                                    <Skeleton variant="text" width="50%" />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                ))}
            </Grid>
        )
    }

    return (
        <Grid container gap="2rem">
            {data?.length !== 0 ? (
                data?.map((item) => (
                    <Card key={item.id} className={classes.card}>
                        {!item.imageUrl ? (
                            <Grid container className={classes.emptyImage}>
                                <FontAwesomeIcon icon={faImage} size="2x" color="white" />
                            </Grid>
                        ) : (
                            <CardMedia
                                component="img"
                                className={classes.image}
                                image={item.imageUrl}
                                alt="Digital signage"
                            />
                        )}
                        <CardContent className={classes.cardContent}>
                            <Grid container alignItems="center" spacing={1}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">
                                        {item.name}
                                    </Typography>
                                </Grid>
                                <Grid item xs={4} display="flex" justifyContent="flex-end">
                                    <SignsStatusChip status={item.statusCode} />
                                </Grid>
                                <Grid item xs={2} display="flex" justifyContent="flex-end">
                                    <IconButton
                                        onMouseEnter={(e) => handleMenuOpen(e, item)}
                                        onMouseLeave={hoverMenu.scheduleClose}
                                        onClick={(e) => handleMenuOpen(e, item)}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                    <Popper
                                        open={hoverMenu.isOpen}
                                        anchorEl={hoverMenu.anchorEl}
                                        placement="bottom-end"
                                        transition
                                        className={classes.popper}
                                    >
                                        {({ TransitionProps }) => (
                                            <Grow {...TransitionProps}>
                                                <Paper elevation={8} className={classes.menuPaper}>
                                                    <ClickAwayListener onClickAway={hoverMenu.handleClose}>
                                                        <MenuList
                                                            onMouseEnter={hoverMenu.cancelClose}
                                                            onMouseLeave={hoverMenu.scheduleClose}
                                                        >
                                                            <LocalizedStrict id="merchant-menu-planning-digital-signage-menu-details">
                                                                <MenuItem onClick={handleViewDetails}>Details</MenuItem>
                                                            </LocalizedStrict>
                                                            <LocalizedStrict id="merchant-menu-planning-digital-signage-menu-clear-sign">
                                                                <MenuItem onClick={() => handleClearSign(item.id)}>
                                                                    Clear sign
                                                                </MenuItem>
                                                            </LocalizedStrict>
                                                        </MenuList>
                                                    </ClickAwayListener>
                                                </Paper>
                                            </Grow>
                                        )}
                                    </Popper>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                ))
            ) : (
                <Grid item xs={12} display="flex" justifyContent="center">
                    <LocalizedStrict id="merchant-menu-planning-digital-signage-empty">
                        <Typography textAlign="center" variant="h5" color="textSecondary">
                            No digital signage devices found
                        </Typography>
                    </LocalizedStrict>
                </Grid>
            )}

            {isModalOpen && (
                <DigitalSignageDialog digitalSignage={selectedItem} open={isModalOpen} onClose={handleModalClose} />
            )}
        </Grid>
    )
}

